import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Badge,
  IconButton,
  useToast,
  useColorModeValue,
  Tooltip,
  Box,
  Icon,
  Center,
  Spinner
} from '@chakra-ui/react';
import { FaDownload, FaEye, FaTrash, FaHistory, FaClock } from 'react-icons/fa';
import axios from 'axios';

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_VERCEL_API_URL || 'https://api.veridicus.com.br'
  : process.env.REACT_APP_LOCAL_API_URL || 'http://localhost:5000';

const VersionHistory = ({ isOpen, onClose, document, clienteId, onVersionChange }) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const toast = useToast();
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const bgColor = useColorModeValue('gray.50', 'gray.700');
  const userType = localStorage.getItem('userType') || '';
  const canDelete = ['master', 'diretoria', 'associado'].includes(userType);

  useEffect(() => {
    if (isOpen && document) {
      fetchVersions();
    }
  }, [isOpen, document]);

  const fetchVersions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(
        `${API_BASE_URL}/api/clientes/${clienteId}/documents/${document._id}/versions`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setVersions(response.data.data);
    } catch (error) {
      console.error('Error fetching versions:', error);
      toast({
        title: 'Erro ao carregar versões',
        description: error.response?.data?.message || 'Erro ao carregar histórico de versões',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadVersion = async (versionDoc) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/api/clientes/${clienteId}/documents/${versionDoc._id}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `v${versionDoc.version}_${versionDoc.originalName}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading version:', error);
      toast({
        title: 'Erro ao baixar versão',
        description: 'Erro ao baixar esta versão do documento',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDeleteVersion = async (versionDoc) => {
    if (!window.confirm(`Tem certeza que deseja excluir a versão ${versionDoc.version} deste documento?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_BASE_URL}/api/clientes/${clienteId}/documents/${versionDoc._id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast({
        title: 'Versão excluída',
        description: 'Versão do documento excluída com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      fetchVersions();
      if (onVersionChange) onVersionChange();
    } catch (error) {
      console.error('Error deleting version:', error);
      toast({
        title: 'Erro ao excluir versão',
        description: error.response?.data?.message || 'Erro ao excluir versão',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (date) => {
    const d = new Date(date);
    return d.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <VStack align="start" spacing={1}>
            <Text>Histórico de Versões</Text>
            <Text fontSize="sm" fontWeight="normal" color="gray.500">
              {document?.originalName}
            </Text>
          </VStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {loading ? (
            <Center p={10}>
              <Spinner size="lg" color="green.500" />
            </Center>
          ) : versions.length === 0 ? (
            <Center p={10}>
              <Text color="gray.500">Nenhuma versão encontrada</Text>
            </Center>
          ) : (
            <Box overflowX="auto">
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Versão</Th>
                    <Th>Status</Th>
                    <Th>Descrição</Th>
                    <Th>Tamanho</Th>
                    <Th>Enviado por</Th>
                    <Th>Data</Th>
                    <Th>Ações</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {versions.map((version, index) => (
                    <Tr key={version._id} bg={version.isLatest ? bgColor : 'transparent'}>
                      <Td>
                        <HStack>
                          <Text fontWeight="medium">v{version.version}</Text>
                          {version.isLatest && (
                            <Badge colorScheme="green" size="sm">
                              Atual
                            </Badge>
                          )}
                        </HStack>
                      </Td>
                      <Td>
                        <Badge
                          colorScheme={version.isLatest ? 'green' : 'gray'}
                          variant={version.isLatest ? 'solid' : 'subtle'}
                        >
                          {version.isLatest ? 'Ativa' : 'Anterior'}
                        </Badge>
                      </Td>
                      <Td>
                        <Text noOfLines={2} fontSize="sm">
                          {version.description}
                        </Text>
                      </Td>
                      <Td>{formatFileSize(version.size)}</Td>
                      <Td>{version.uploadedBy?.nomeCompleto || 'N/A'}</Td>
                      <Td>
                        <Tooltip label={formatDate(version.createdAt)}>
                          <Text fontSize="sm">{formatDate(version.createdAt)}</Text>
                        </Tooltip>
                      </Td>
                      <Td>
                        <HStack spacing={2}>
                          <Tooltip label="Baixar esta versão">
                            <IconButton
                              size="sm"
                              icon={<FaDownload />}
                              variant="ghost"
                              onClick={() => handleDownloadVersion(version)}
                            />
                          </Tooltip>
                          {canDelete && !version.isLatest && (
                            <Tooltip label="Excluir esta versão">
                              <IconButton
                                size="sm"
                                icon={<FaTrash />}
                                variant="ghost"
                                colorScheme="red"
                                onClick={() => handleDeleteVersion(version)}
                              />
                            </Tooltip>
                          )}
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}

          {versions.length > 0 && (
            <Box mt={6} p={4} borderWidth="1px" borderColor={borderColor} borderRadius="md">
              <HStack spacing={4} fontSize="sm" color="gray.600">
                <HStack>
                  <Icon as={FaHistory} />
                  <Text>Total de versões: {versions.length}</Text>
                </HStack>
                <HStack>
                  <Icon as={FaClock} />
                  <Text>
                    Primeira versão: {versions.length > 0 && formatDate(versions[versions.length - 1].createdAt)}
                  </Text>
                </HStack>
              </HStack>
            </Box>
          )}
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>Fechar</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default VersionHistory;
